//#region imports
import React, { useState } from 'react';
import { Button, Modal, Form, Badge } from 'react-bootstrap';

// styling
import { Colors } from '_styles';
//#endregion

const ROLE_OPTIONS = [
    { value: 'REPORTS_ONLY', label: 'Reports' },
    { value: 'SECURITY', label: 'Security' },
    { value: 'ALLOCATOR', label: 'Allocator' },
    { value: 'DESK_HOST', label: 'Desk Host' },
    { value: 'OPERATIONS_ADMINISTRATOR', label: 'Operations Administrator' },
];

const EmployeeRow = ({ employee, isCurrentUser, onRoleChange, onDelete, onResendInvite, onRequireMfa, onSendPasswordReset, loading = false, selected = false, onSelectToggle }) => {

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const isMissingFirebase = employee.missingFirebaseAccount === true;

    const displayName = employee.name && employee.name !== 'N/A'
        ? employee.name
        : employee.email;

    const initials = (employee.name && employee.name !== 'N/A' ? employee.name : employee.email || '?')
        .split(' ')
        .map(part => part.charAt(0))
        .slice(0, 2)
        .join('')
        .toUpperCase();

    const roleInOptions = ROLE_OPTIONS.some(option => option.value === employee.role);
    const showRequireMfa = !employee.mfaEnrolled && !employee.mfaRequired;

    return (
        <li className="list-group-item d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">

                {/* selection checkbox for bulk actions */}
                {!isCurrentUser ? (
                    <Form.Check
                        type="checkbox"
                        checked={selected}
                        onChange={() => onSelectToggle(employee.id)}
                        className="me-2"
                        disabled={loading}
                    />
                ) : (
                    <div className="me-2" style={{ width: 16 }} />
                )}

                {/* initials */}
                <div
                    className="d-flex align-items-center justify-content-center me-3"
                    style={{
                        width: 45,
                        height: 45,
                        borderRadius: '50%',
                        backgroundColor: Colors.PRM_LIGHT_BLUE,
                        color: 'white',
                        fontWeight: 'bold',
                        flexShrink: 0,
                    }}
                >
                    {initials}
                </div>

                <div>
                    <div className="fw-semibold">
                        {displayName}
                        {isCurrentUser && <span className="text-muted small ms-2">(you)</span>}
                        {isMissingFirebase && (
                            <Badge bg="danger" className="ms-2">Missing Firebase Account</Badge>
                        )}
                        {!isMissingFirebase && employee.pending && (
                            <Badge bg="warning" text="dark" className="ms-2">Setup Pending</Badge>
                        )}
                        {!isMissingFirebase && employee.mfaEnrolled && (
                            <Badge bg="success" className="ms-2">MFA On</Badge>
                        )}
                        {!isMissingFirebase && !employee.mfaEnrolled && employee.mfaRequired && (
                            <Badge bg="info" className="ms-2">MFA Required</Badge>
                        )}
                    </div>
                    <div className="text-muted small">
                        {employee.email}
                    </div>
                </div>
            </div>

            <div className="d-flex align-items-center gap-2">

                {/* role selector (falls back to a label for non-standard roles) */}
                {!isMissingFirebase && roleInOptions && !isCurrentUser ? (
                    <Form.Select
                        size="sm"
                        value={employee.role}
                        onChange={(e) => onRoleChange(employee.id, e.target.value)}
                        disabled={loading}
                        style={{ width: 220 }}
                    >
                        {ROLE_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>
                ) : !isMissingFirebase ? (
                    <Badge bg="secondary">{employee.role}</Badge>
                ) : null}

                {/* resend invite for pending accounts */}
                {!isMissingFirebase && employee.pending && (
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => onResendInvite(employee.id)}
                        disabled={loading}
                    >
                        Resend Invite
                    </Button>
                )}

                {/* require password reset + MFA for accounts that do not have MFA yet */}
                {!isMissingFirebase && showRequireMfa && (
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => onRequireMfa(employee.id)}
                        disabled={loading}
                    >
                        Require MFA & Password Reset
                    </Button>
                )}

                {/* send password reset email for accounts with MFA already set up */}
                {!isMissingFirebase && employee.mfaEnrolled && !isCurrentUser && (
                    <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => onSendPasswordReset(employee.email)}
                        disabled={loading}
                    >
                        Reset Password
                    </Button>
                )}

                {/* remove / delete member */}
                {!isCurrentUser && (
                    <Button
                        variant={isMissingFirebase ? "danger" : "outline-danger"}
                        size="sm"
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={loading}
                    >
                        {isMissingFirebase ? 'Delete' : 'Remove'}
                    </Button>
                )}
            </div>

            {/* delete confirmation modal */}
            <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{isMissingFirebase ? 'Delete Orphaned Account' : 'Remove Member'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isMissingFirebase ? (
                        <>
                            <p>
                                This account (<strong>{employee.firebaseId}</strong>) has no corresponding Firebase account.
                            </p>
                            <p className="text-muted small">
                                This will delete the database record only. There is no Firebase account to remove.
                            </p>
                        </>
                    ) : (
                        <>
                            <p>
                                Are you sure you want to remove <strong>{displayName}</strong> from the team?
                            </p>
                            <p className="text-muted small">
                                This will delete their account and remove their access to the portal.
                            </p>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => { onDelete(employee.id); setShowDeleteConfirm(false); }}
                    >
                        {isMissingFirebase ? 'Delete Record' : 'Remove Member'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </li>
    );
};

export default EmployeeRow;
